import React, {useRef, useEffect, useState, ReactNode, useMemo} from "react";
import {useFrame, useThree} from "@react-three/fiber";
import * as THREE from "three";
import {toCanvas} from "html-to-image";
import {createRoot, Root} from "react-dom/client";
import {SleepAsync, Timer} from "js-vextensions";
import {vrLayers} from "./VRLayers.js";
import {useXRInputSourceState} from "@react-three/xr";
import {raycaster_allLayers, raycaster_main} from "../../../UI/Tools/VR/Utils/Raycasters.js";
import {getControllerPosAndDir} from "./Controller.js";
import {PrepForFastRaycasting} from "../../../UI/Tools/VR/Utils/ModelProcessor.js";
import {OnStickDirectionalStateChanged, useStickDirectionalStateChangeListener} from "./XRHooks.js";
import {useForceUpdate} from "web-vcore";
import {flushSync} from "react-dom";
import {GetControllerSide_Final} from "../../../Store/main/settings/vr.js";
import {observer_mgl_r3} from "../General.js";

interface HTMLInVRProps {
	children: ReactNode;
	width?: number;
	height?: number;
	pixelsPerMeter?: number;
	position?: THREE.Vector3 | [number, number, number];
	rotation?: THREE.Euler | [number, number, number];
	canvasPixelRatio?: number;
	backgroundColor?: string;

	//redrawDeps: any[];
	vrActive: boolean;
	onPointerDown?: (side: "left" | "right")=>void;
	onStickDirectionalStateChanged?: OnStickDirectionalStateChanged;
}

export const HTMLInVR: React.FC<HTMLInVRProps> = observer_mgl_r3((props: HTMLInVRProps)=>{
	const {
		children, width = 1, height = 1, pixelsPerMeter = 100, position = [0, 0, 0], rotation = [0, 0, 0], canvasPixelRatio = 3, backgroundColor = "transparent",
		/*redrawDeps,*/ vrActive, onPointerDown, onStickDirectionalStateChanged,
	} = props;
	const meshRef = useRef<THREE.Mesh>(null);
	const [childRenderContainer, setChildRenderContainer] = useState<HTMLDivElement | null>(null);
	const [childRenderContainerReactRoot, setChildRenderContainerReactRoot] = useState<Root | null>(null);

	// Create a hidden div to render the HTML
	useEffect(()=>{
		if (!childRenderContainer) {
			const div = document.createElement("div");
			div.style.position = "absolute";
			div.style.left = "-9999px";
			div.style.top = "-9999px";
			div.style.width = `${width * pixelsPerMeter}px`;
			div.style.height = `${height * pixelsPerMeter}px`;
			div.style.overflow = "hidden";
			div.style.pointerEvents = "none";
			div.style.backgroundColor = backgroundColor;
			document.body.appendChild(div);
			setChildRenderContainer(div);
			setChildRenderContainerReactRoot(createRoot(div));
		}
		return ()=>{
			if (childRenderContainer) {
				document.body.removeChild(childRenderContainer);
			}
		};
	}, [childRenderContainer, width, height, pixelsPerMeter, backgroundColor]);

	const [texture, setTexture] = useState<THREE.CanvasTexture | null>(null);
	// dispose texture on component unmount
	useEffect(()=>{
		return ()=>{
			if (texture) texture.dispose();
		};
	}, [texture]);

	const childrenHolder_mutatableInterface = useMemo(()=>({latestChildren: children, triggerNextUpdate: null} as HTMLInVR_ChildrenHolder_MutatableInterface), []);
	childrenHolder_mutatableInterface.latestChildren = children;

	// render HTML to the hidden div, then render that to a new canvas, then set canvas-texture to target that canvas as its new image-source
	const childrenJSON = JSON.stringify(children?.["props"]); // this doesn't work in every case; but it works here (atm it's just one child, of type DreamEventsUI [see DreamPanel.tsx], with props that are all json-serializable)
	useEffect(()=>{
		proceed();
		async function proceed() {
			if (!childRenderContainer) return;
			if (!childRenderContainerReactRoot) return;

			if (childrenHolder_mutatableInterface.triggerNextUpdate == null) {
				childRenderContainerReactRoot.render(<HTMLInVR_ChildrenHolder mutatableInterface={childrenHolder_mutatableInterface}/>);
				for (let i = 0; i < 5000 / 10 && childRenderContainer.childNodes.length == 0; i++) {
					await SleepAsync(10);
				}
			} else {
				childrenHolder_mutatableInterface.triggerNextUpdate!();
			}

			try {
				// Convert HTML to Canvas directly
				if (childRenderContainer.childNodes[0]?.ownerDocument == null) {
					console.error("Error rendering HTML to canvas; childRenderContainer.childNodes[0]?.ownerDocument was null.");
					return;
				}
				const canvas = await toCanvas(childRenderContainer.childNodes[0] as HTMLElement, {
					quality: 1,
					pixelRatio: canvasPixelRatio,
					skipFonts: true,
					//fontEmbedCSS: true,
					backgroundColor,
				});
				//console.log("Draw done. @canvas:", canvas);
				//await SleepAsync(100);

				// NOTE: Enabling this can be nice for debugging, BUT it causes threejs to have a shader-compile error. (I think the problem is adding these creates scroll-bar, which changes threejs canvas size, causing rerender or something)
				//canvas.id = `htmlCanvas_${Date.now()}`;
				//document.body.appendChild(canvas);

				// update to a new texture, with the new canvas as the source
				const newTexture = new THREE.CanvasTexture(canvas);
				newTexture.minFilter = THREE.LinearFilter;
				newTexture.colorSpace = THREE.SRGBColorSpace;
				newTexture.needsUpdate = true;
				setTexture(newTexture);
			} catch (error) {
				console.error("Error rendering HTML to canvas:", error);
			}
		};
	}, [childRenderContainer, childRenderContainerReactRoot, childrenJSON]);

	const {gl} = useThree();
	const rightController = vrActive ? useXRInputSourceState("controller", GetControllerSide_Final("right")) : null;
	const controllerLastPressedStates = new Map<string, boolean>();
	useFrame(()=>{
		if (!gl.xr.isPresenting || gl.xr.getSession()?.inputSources == null) return;
		const session = gl.xr.getSession()!;
		for (const inputSource of session.inputSources) {
			const side = inputSource.handedness;
			if (side != "left" && side != "right") continue;

			const pressed_prev = controllerLastPressedStates.get(side);
			const pressed = inputSource.gamepad?.buttons[0]?.pressed ?? false;
			if (pressed && !pressed_prev && onPointerDown) {
				const {pos: controllerPos, dir: controllerDir} = getControllerPosAndDir(rightController, GetControllerSide_Final("right"));
				if (controllerPos == null || controllerDir == null || meshRef.current == null) return;

				raycaster_allLayers.set(controllerPos, controllerDir); // Set up and cast the ray
				const directionEndRaycast_hits = raycaster_allLayers.intersectObject(meshRef.current);
				if (directionEndRaycast_hits.length == 0) return;

				//const target_end = directionEndRaycast_hits[0].point;
				onPointerDown(side);
			}
			controllerLastPressedStates.set(side, pressed);
		}
	});

	useStickDirectionalStateChangeListener(onStickDirectionalStateChanged);

	return (
		<mesh ref={meshRef} position={position} rotation={rotation}
			// this doesn't do enough, because I can't control the layers of the teleport raycasts (easily) atm
			//layers={[vrLayers.noRaycast]}
			// so for now, I just disable raycasts for this object from any source (except for raycasters specifically marked with HTMLInVR_allowHit)
			raycast={(raycaster, intersects)=>{
				if (!raycaster["HTMLInVR_allowHit"]) return false;
				if (meshRef.current == null) return false;

				// this part commented; `return true` already takes care of recursion/result-population
				/*intersects.length = 0;
				raycaster.intersectObject(meshRef.current, true, intersects);*/

				//return true;

				if (!meshRef.current["PrepForFastRaycasting_called"]) {
					PrepForFastRaycasting(meshRef.current);
					meshRef.current["PrepForFastRaycasting_called"] = true;
				}
				return meshRef.current.constructor.prototype.raycast.call(meshRef.current, raycaster, intersects);
			}}
		>
			<planeGeometry args={[width, height]}/>
			<meshStandardMaterial map={texture} transparent={backgroundColor === "transparent"} side={THREE.DoubleSide}

				//transparent={true}
				//opacity={0.5} // Your desired opacity
				/*depthWrite={false} // Critical for multiple transparent layers
				blending={THREE.CustomBlending}
				blendSrc={THREE.SrcAlphaFactor}
				blendDst={THREE.OneMinusSrcAlphaFactor}
				blendEquation={THREE.AddEquation}*/

				alphaTest={0} // Adjust based on your texture
			/>
		</mesh>
	);
});

export type HTMLInVR_ChildrenHolder_MutatableInterface = {latestChildren: ReactNode, triggerNextUpdate: (()=>any)|n};
export const HTMLInVR_ChildrenHolder = (props: {mutatableInterface: HTMLInVR_ChildrenHolder_MutatableInterface})=>{
	const {mutatableInterface} = props;

	const forceUpdate = useForceUpdate();
	mutatableInterface.triggerNextUpdate = ()=>{
		// flushSync ensures the update is applied immediately (so that the html-to-image can draw it to canvas immediately, reducing delay in it showing up for the user)
		flushSync(()=>{
			forceUpdate();
		});
	};

	return mutatableInterface.latestChildren;
};